import {
    Button,
    MultipleTextField,
    SelectField,
    TextField,
    Typography,
    useAlert
} from "matsuri-ui"
import { css } from "@emotion/react"
import dayjs from "dayjs"
import {
    ROUTE_DESCRIPTION_MAX_CHARS,
    countRouteDescriptionChars,
    displayExpenseDate,
    minSelectableExpenseDate,
    validateExpenseDate,
    validateExpenseInput
} from "@/model/transportationExpense"
import { DateString, formatDate } from "@/utils/dateString"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"

export const TransportationExpenseForm = ({
    expenseDate,
    onChangeExpenseDate,
    linkedCleaningNames,
    hasCleaningsOnDate,
    isLoadingCleanings,
    isLoadingExpenses,
    alreadyRegistered,
    amountRaw,
    onChangeAmountRaw,
    routeDescription,
    onChangeRouteDescription,
    onNext
}: {
    expenseDate: DateString
    onChangeExpenseDate: (date: DateString) => void
    linkedCleaningNames: string[]
    hasCleaningsOnDate: boolean
    isLoadingCleanings: boolean
    isLoadingExpenses: boolean
    alreadyRegistered: boolean
    amountRaw: string
    onChangeAmountRaw: (value: string) => void
    routeDescription: string
    onChangeRouteDescription: (value: string) => void
    onNext: (input: { amount: number; routeDescription: string }) => void
}) => {
    const { t } = useTranslation()
    const { throwAlert } = useAlert()

    const today = formatDate(dayjs())
    const expenseDateResult = validateExpenseDate(expenseDate, today)
    const noCleaningsOnDate = !isLoadingCleanings && !hasCleaningsOnDate

    return (
        <form
            onSubmit={event => {
                event.preventDefault()

                if (
                    alreadyRegistered ||
                    isLoadingCleanings ||
                    isLoadingExpenses ||
                    noCleaningsOnDate
                ) {
                    return
                }

                if (!expenseDateResult.ok) {
                    throwAlert(new Error(t(expenseDateResult.message)), {
                        errorMessage: { happend: t(expenseDateResult.message) }
                    })
                    return
                }

                const inputResult = validateExpenseInput(
                    amountRaw,
                    routeDescription
                )
                if (!inputResult.ok) {
                    throwAlert(new Error(t(inputResult.message)), {
                        errorMessage: { happend: t(inputResult.message) }
                    })
                    return
                }

                onNext(inputResult.value)
            }}
            css={css`
                display: grid;
                gap: 16px;
            `}
        >
            <SelectField
                name="expenseDate"
                label={t("清掃日")}
                value={expenseDate}
                required
                options={[
                    {
                        value: minSelectableExpenseDate(today),
                        label: displayExpenseDate(
                            minSelectableExpenseDate(today)
                        )
                    },
                    {
                        value: today,
                        label: displayExpenseDate(today)
                    }
                ]}
                onChange={value => {
                    if (value) {
                        onChangeExpenseDate(value)
                    }
                }}
                helperText={t("当日または前日の分のみ申請できます")}
            />

            <div
                css={css`
                    display: grid;
                    gap: 4px;
                `}
            >
                <Typography variant="caption">
                    {t("アサインされている清掃")}
                </Typography>
                <div
                    css={css`
                        padding: 12px 16px;
                        color: ${
                            linkedCleaningNames.length > 0
                                ? theme.palette.intentions.text.primary
                                : theme.palette.gray[500]
                        };
                        background-color: ${theme.palette.gray[100]};
                        border: 1px solid ${theme.palette.gray[300]};
                        border-radius: 8px;
                    `}
                >
                    <Typography variant="body2">
                        {isLoadingCleanings
                            ? t("読み込み中...")
                            : linkedCleaningNames.length > 0
                              ? linkedCleaningNames.join(" / ")
                              : t("この日にアサインされた清掃はありません")}
                    </Typography>
                </div>
            </div>

            {alreadyRegistered && (
                <Typography
                    css={css`
                        color: ${theme.palette.intentions.secondary.main};
                    `}
                >
                    {t(
                        "この日の交通費はすでに申請済みです。修正する場合は申請履歴から修正してください"
                    )}
                </Typography>
            )}

            {noCleaningsOnDate && (
                <Typography
                    css={css`
                        color: ${theme.palette.intentions.secondary.main};
                    `}
                >
                    {t("この日にアサインされた清掃がないため申請できません")}
                </Typography>
            )}

            {!expenseDateResult.ok && (
                <Typography
                    css={css`
                        color: ${theme.palette.intentions.secondary.main};
                    `}
                >
                    {t(expenseDateResult.message)}
                </Typography>
            )}

            <MultipleTextField
                name="routeDescription"
                label={t("経路")}
                placeholder={t("例: 宮根→蕨→要町→北池袋→蕨→宮根")}
                value={routeDescription}
                onChange={value => onChangeRouteDescription(value)}
                required
                helperText={({ value }) =>
                    `${countRouteDescriptionChars(value ?? "")}/${ROUTE_DESCRIPTION_MAX_CHARS}`
                }
            />

            <TextField
                name="amount"
                label={t("合計金額（円）")}
                placeholder={t("例: 780")}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={amountRaw}
                onChange={value => onChangeAmountRaw(value)}
                required
            />

            <Button
                variant="filled"
                color="primary"
                type="submit"
                disabled={
                    alreadyRegistered ||
                    isLoadingCleanings ||
                    // 申請済み判定が確定するまで進ませない
                    isLoadingExpenses ||
                    noCleaningsOnDate ||
                    !expenseDateResult.ok
                }
            >
                {t("確認へ進む")}
            </Button>
        </form>
    )
}
